import React, { FunctionComponent, useRef, useState } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap } from 'three';
import { hasEntityTapAction, transitionAdjustedRenderOrder } from '../../utils';
import { useDataStore, useSceneStore, useUserStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IButton, IButtonReactProps, IButtonSubCategory } from '@r3f-component-data-structure';
import { EntityErrorBoundary } from '@r3f/components/boundaries';
import {
	useGetAdjustedFontSize,
	useGetAdjustedBorderWidth,
	useGetAdjustedMemoSpatialProps,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useHocPointerDown,
	useOnEntityTestClick,
	useGetEntityIsHidden,
	useResetEntityTransformsOnPlacement,
} from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';

interface IButtonWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withButtonBehaviour = (WrappedButton: FunctionComponent<IButtonReactProps>): FunctionComponent<IButtonWrapped> => {
	const FuncComp: FunctionComponent<IButtonWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const button = useDataStore(({ componentsById }) => componentsById?.[id] as IButton) || {};
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true );
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		// ! Don't remove this line - it's a quick-fix to deliberately cause a re-render to keep render order in sync with Video components
		const sceneTransitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);
		const [isMounted, setIsMounted] = useState(false);
		const onMount = () => setIsMounted(true);
		const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);

		useOnEntityTestClick({ sceneId, entity: button, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer({ object3d: pointerGroupRef, enabled: hasEntityTapAction(button) && isMounted, layer: typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1 });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);
		
		// Get entity values
		const fontSize = useGetAdjustedFontSize(button, screenRelative);
		const borderWidth = useGetAdjustedBorderWidth(button, screenRelative);
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (button.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(button, screenRelative);
		const { category, borderRadius, borderRgba, text, fontRgba, opacity, color, fontFamily, textAlignment, textureUrl, svgUrl, subCategory, curvature, castShadow = false, receiveShadow = false, isSnappedToTarget = false, depthTest = true, billboard } = button;
		const isSnapshotButton = subCategory && [IButtonSubCategory.recording, IButtonSubCategory.snapshot].includes(subCategory);
		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: button, screenRelative });
		
		useInteraction(pointerGroupRef, 'onSelect', e => {
			onPointerDown(e.intersections);
			pointerIsDownRef.current = false;
		})
		
		const entityIsHidden = (useGetEntityIsHidden(id) && screenRelative) || isSnapshotButton;

		return (
			<group name="ENTITY_VISIBLE_GROUP" visible={!entityIsHidden}>
				<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} entityId={id} position={zeroTuple3} screenRelative={screenRelative}>
					<EntityErrorBoundary renderOrder={renderOrder} scale={scale} rotation={rotation} position={position}>
						<group name={`toggleVisibilityGroup-${id}`}>
							<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
								<WrappedButton
									id={id}
									renderOrder={renderOrder}
									nonTransientScale={scale}
									rotation={rotation}
									position={position}
									scale={scale}
									curvature={curvature}
									borderRadius={borderRadius}
									borderRgba={borderRgba}
									text={text}
									fontRgba={fontRgba}
									category={category}
									color={color}
									onMount={onMount}
									borderWidth={borderWidth}
									fontSize={fontSize}
									fontFamily={fontFamily}
									opacity={opacity}
									textAlignment={textAlignment}
									textureUrl={textureUrl}
									svgUrl={svgUrl}
									castShadow={shadowsEnabled && castShadow}
									receiveShadow={shadowsEnabled && receiveShadow}
									raycast={raycast}
									onPointerDown={(e) => onPointerDown(e.intersections)}
									animationTransformGroupPrefix='transformGroup-'
									depthTest={depthTest}
									billboard={billboard}
									isCameraOrbiting={isGrabAndGoActive}
									rotateNoBillboardOuterGroup={false}
								/>
							</group>
						</group>
					</EntityErrorBoundary>
				</EntityLevelFadeGroup>
			</group>

		);
	};
	return FuncComp;
};

export default withButtonBehaviour;
